import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import PreviewIcon from '@mui/icons-material/Preview';
import { Box, Button, Divider, Drawer, Stack, Typography } from '@mui/material';
import { type FC, useEffect, useState } from 'react';

import { useSettings } from '@hooks/useSettings';
import { useUpdateSettingsUser } from '@hooks/useUpdateSettingsUser';
import { ClickableBox } from '@uiKit/ClickableBox';
import { DETAILS, PREVIEW, TABLE_IMAGE } from 'src/constants';

import { drawerPaperSx, optionBoxSx } from './styles';

type ImageBehavior = typeof PREVIEW | typeof DETAILS;

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const SettingsDrawer: FC<SettingsDrawerProps> = ({ open, onClose }) => {
  const settings = useSettings([TABLE_IMAGE]) as Record<string, string>;
  const { updateSettingsUser, isLoading } = useUpdateSettingsUser();

  const savedValue = (settings[TABLE_IMAGE] as ImageBehavior) || PREVIEW;
  const [imageBehavior, setImageBehavior] = useState<ImageBehavior>(savedValue);

  useEffect(() => {
    if (open) {
      setImageBehavior(savedValue);
    }
  }, [open, savedValue]);

  const isDirty = imageBehavior !== savedValue;

  const handleSave = async () => {
    await updateSettingsUser([TABLE_IMAGE], { [TABLE_IMAGE]: imageBehavior });
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: drawerPaperSx } }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">Settings</Typography>
      </Box>

      <Divider />

      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, mt: 1 }}>
          Table Image Behavior
        </Typography>

        <Stack spacing={1.5}>
          <ClickableBox
            role="button"
            tabIndex={0}
            onClick={() => setImageBehavior(PREVIEW)}
            sx={optionBoxSx(imageBehavior === PREVIEW)}
          >
            <ImageSearchIcon sx={{ color: 'text.primary' }} />
            <Box>
              <Typography variant="subtitle1">Image preview</Typography>
              <Typography variant="body2" color="text.secondary">
                Clicking a table image opens a preview of the image
              </Typography>
            </Box>
          </ClickableBox>

          <ClickableBox
            role="button"
            tabIndex={0}
            onClick={() => setImageBehavior(DETAILS)}
            sx={optionBoxSx(imageBehavior === DETAILS)}
          >
            <PreviewIcon sx={{ color: 'text.primary' }} />
            <Box>
              <Typography variant="subtitle1">Item Details page</Typography>
              <Typography variant="body2" color="text.secondary">
                Clicking a table image opens the Item Details page
              </Typography>
            </Box>
          </ClickableBox>
        </Stack>
      </Box>

      <Box sx={{ mt: 'auto', p: 2 }}>
        <Button
          size="large"
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSave}
          disabled={!isDirty || isLoading}
        >
          Save Changes
        </Button>
      </Box>
    </Drawer>
  );
};
